import { Type, Static } from '@sinclair/typebox'

import { Trees, SupplementaryFeedT, ConsumablesRecordT, CattlePurchasesT, Pasture_PreV3_5T, Pasture, NumberMinZeroDefaultUndefinedTB, CattleTransportTruckTypeTB, CattlePurchaseTB } from "./report_shared"

export const FeedlotRationsFeedTypeGrainsTB = Type.Literal('grains')
export const FeedlotRationsFeedTypeConcentratesTB = Type.Literal('concentrates')
export const FeedlotRationsFeedTypeGrassesTB = Type.Literal('grasses')
export const FeedlotRationsFeedTypeLegumesTB = Type.Literal('legumes')

export const FeedlotRationsFeedTypeTB = Type.Union([
  FeedlotRationsFeedTypeGrainsTB,
  FeedlotRationsFeedTypeConcentratesTB,
  FeedlotRationsFeedTypeGrassesTB,
  FeedlotRationsFeedTypeLegumesTB
])
export type FeedlotRationsFeedTypeT = Static<typeof FeedlotRationsFeedTypeTB>

export const FeedlotRationsFeedTypeMapTB = Type.Object({
  'Grains (inc molasses)': FeedlotRationsFeedTypeGrainsTB,
  'Other Concentrates': FeedlotRationsFeedTypeConcentratesTB,
  'Grasses': FeedlotRationsFeedTypeGrassesTB,
  'Legumes': FeedlotRationsFeedTypeLegumesTB
})
export type FeedlotRationsFeedTypeMapT = Static<typeof FeedlotRationsFeedTypeMapTB>

// TODO: some better way of not having to write all these out
export const FeedlotRationsAllocationFormTB = Type.Object({
  grains: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  concentrates: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  grasses: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  legumes: NumberMinZeroDefaultUndefinedTB({ maximum: 100 })
})
export type FeedlotRationsAllocationFormT = Static<typeof FeedlotRationsAllocationFormTB>

export const FeedlotRationsAllocationRecordTB = Type.Object({
  grains: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  concentrates: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  grasses: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  legumes: NumberMinZeroDefaultUndefinedTB({ maximum: 1 })
})
export type FeedlotRationsAllocationRecordT = Static<typeof FeedlotRationsAllocationRecordTB>

function transformFeedlotRationsAllocationRecordToForm (record: FeedlotRationsAllocationRecordT): FeedlotRationsAllocationFormT {
  return {
    grains: record.grains * 100,
    concentrates: record.concentrates * 100,
    grasses: record.grasses * 100,
    legumes: record.legumes * 100
  };
}
function transformFeedlotRationsAllocationFormToRecord (form: FeedlotRationsAllocationFormT): FeedlotRationsAllocationRecordT {
  return {
    grains: form.grains / 100,
    concentrates: form.concentrates / 100,
    grasses: form.grasses / 100,
    legumes: form.legumes / 100
  };
}

export const FeedlotRationsFormTB = Type.Array(Type.Object({
  id: Type.String(),
  name: Type.String({format: 'clean-string'}),
  dryMatterDigestibility: NumberMinZeroDefaultUndefinedTB({ minimum: 25, maximum: 100 }),
  crudeProtein: NumberMinZeroDefaultUndefinedTB({ minimum: 2, maximum: 100 }),
  dietaryNetEnergyConcentration: NumberMinZeroDefaultUndefinedTB({ minimum: 2, maximum: 30 }),
  nitrogenRetention: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  feedComponents: Type.Object({
    proportions: FeedlotRationsAllocationFormTB,
    cellulose: FeedlotRationsAllocationFormTB,
    hemicellulose: FeedlotRationsAllocationFormTB,
    solubleResidue: FeedlotRationsAllocationFormTB,
    nitrogen: FeedlotRationsAllocationFormTB
  })
}), { minItems: 1, default: [] })
export type FeedlotRationsFormT = Static<typeof FeedlotRationsFormTB>

export const FeedlotRationsRecordTB = Type.Object({
  id: Type.String(),
  name: Type.String({format: 'clean-string'}),
  dryMatterDigestibility: NumberMinZeroDefaultUndefinedTB({ minimum: 0.25, maximum: 1 }),
  crudeProtein: NumberMinZeroDefaultUndefinedTB({ minimum: 0.02, maximum: 1 }),
  dietaryNetEnergyConcentration: NumberMinZeroDefaultUndefinedTB({ minimum: 2, maximum: 30 }),
  nitrogenRetention: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  feedComponents: Type.Object({
    proportions: FeedlotRationsAllocationRecordTB,
    cellulose: FeedlotRationsAllocationRecordTB,
    hemicellulose: FeedlotRationsAllocationRecordTB,
    solubleResidue: FeedlotRationsAllocationRecordTB,
    nitrogen: FeedlotRationsAllocationRecordTB
  })
})
export type FeedlotRationsRecordT = Static<typeof FeedlotRationsRecordTB>

// transform functions from db -> client form and client form -> db
export function transformFeedlotRationsRecordsToForm(records: Array<FeedlotRationsRecordT>): FeedlotRationsFormT {
  return records.map(r => ({
    ...r,
    dryMatterDigestibility: r.dryMatterDigestibility * 100,
    crudeProtein: r.crudeProtein * 100,
    nitrogenRetention: r.nitrogenRetention * 100,
    feedComponents: {
      proportions: transformFeedlotRationsAllocationRecordToForm(r.feedComponents.proportions),
      cellulose: transformFeedlotRationsAllocationRecordToForm(r.feedComponents.cellulose),
      hemicellulose: transformFeedlotRationsAllocationRecordToForm(r.feedComponents.hemicellulose),
      solubleResidue: transformFeedlotRationsAllocationRecordToForm(r.feedComponents.solubleResidue),
      nitrogen: transformFeedlotRationsAllocationRecordToForm(r.feedComponents.nitrogen)
    }
  }))
}
export function transformFeedlotRationsFormToRecord(form: FeedlotRationsFormT): Array<FeedlotRationsRecordT> {
  return form.map(f => ({
    ...f,
    dryMatterDigestibility: f.dryMatterDigestibility / 100,
    crudeProtein: f.crudeProtein / 100,
    nitrogenRetention: f.nitrogenRetention / 100,
    feedComponents: {
      proportions: transformFeedlotRationsAllocationFormToRecord(f.feedComponents.proportions),
      cellulose: transformFeedlotRationsAllocationFormToRecord(f.feedComponents.cellulose),
      hemicellulose: transformFeedlotRationsAllocationFormToRecord(f.feedComponents.hemicellulose),
      solubleResidue: transformFeedlotRationsAllocationFormToRecord(f.feedComponents.solubleResidue),
      nitrogen: transformFeedlotRationsAllocationFormToRecord(f.feedComponents.nitrogen)
    }
  }))
}

export const FeedlotCohortsTB = Type.Array(Type.Object({
  name: Type.String(),
  ration: Type.String(), // an id of a previously defined FeedlotRationsFormTB item
  livestockNumbers: NumberMinZeroDefaultUndefinedTB(),
  averageStay: NumberMinZeroDefaultUndefinedTB(),
  liveweight: NumberMinZeroDefaultUndefinedTB({ maximum: 1500 }),
}), { minItems: 1, default: [] })
export type FeedlotCohortsT = Static<typeof FeedlotCohortsTB>

export const FeedlotCattlePurchasesTB = Type.Array(Type.Composite([
  CattlePurchaseTB,
  Type.Object({
    distanceTransported: NumberMinZeroDefaultUndefinedTB(),
    truckType: CattleTransportTruckTypeTB
  })
]))
export type FeedlotCattlePurchasesT = Static<typeof FeedlotCattlePurchasesTB>

export const FeedlotProductionTB = Type.Object({
  headSold: NumberMinZeroDefaultUndefinedTB(),
  avgLiveweight: NumberMinZeroDefaultUndefinedTB({ maximum: 1500 }),
})
export type FeedlotProductionT = Static<typeof FeedlotProductionTB>


export type FeedlotReport_Components_PreV3_5 = {
    rationsInformation: Ration[]
    feedlotCohortsInformation: FeedlotCohorts[]
    cattlePurchasesInformation: CattlePurchasesT
    supplementaryFeedInformation: SupplementaryFeedT
    consumablesInformation: ConsumablesRecordT
    pastureInformation: Pasture_PreV3_5T
    treesInformation: Trees[]
}

export type FeedlotReport_Components_PreV3_5_Partial = Partial<FeedlotReport_Components_PreV3_5>

export type FeedlotReport_Components = Omit<FeedlotReport_Components_PreV3_5, "pastureInformation"> & {
    pastureInformationPostV3_4: Pasture
    feedlotProductionInformation: FeedlotProduction
}

export type FeedlotReport_Components_Partial = Partial<FeedlotReport_Components>


export type Ration = {
  id: string
  name: string
  dryMatterDigestibility: number
  crudeProtein: number
  dietaryNetEnergyConcentration: number
  nitrogenRetention: number
  feedComponents: FeedComponents
}

export type FeedTypeKeys = "grains" | "concentrates" | "grasses" | "legumes"
export type FeedAllocation = Record<FeedTypeKeys, number>

export type FeedComponents = {
    proportions: FeedAllocation
    cellulose: FeedAllocation
    hemicellulose: FeedAllocation
    solubleResidue: FeedAllocation
    nitrogen:FeedAllocation
}

export type FeedlotCohorts = {
    name: string
    ration: string
    livestockNumbers: number
    averageStay: number
    liveweight: number
}

export type FeedlotProduction = {
    headSold: number
    avgLiveweight: number
}
